import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import { findServiceProduct } from '../../store/serviceProduct/api';
import {
  reset,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../store/serviceProduct/serviceProductSlice';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { encryptString, getAssetURl } from '../../utils/url';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.serviceProduct);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findServiceProduct({
          limit,
          skip,
          ...(search && { search }),
        })
      );
    }
  }, [dispatch, status, search, limit, skip]);

  useEffect(() => {
    dispatch(reset());
  }, [search, dispatch]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <Page title='Event Service Products'>
      <Container maxWidth='xl'>
        <HeaderBreadcrumbs
          heading='Event Service Products'
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              component={RouterLink}
              to='/event-service-product/create'
              startIcon={<Iconify icon='eva:plus-fill' />}
            >
              New Product
            </Button>
          }
        />
        <Card>
          <Searchbar handleSearch={(e: any) => setSearch(e.target.value)} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative', width: '100%' }}>
              <Table size='medium' sx={{ minWidth: 1000, width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Image</TableCell>
                    <TableCell align='center'>Label EN</TableCell>
                    <TableCell align='center'>Vendor</TableCell>
                    <TableCell align='center'>Category</TableCell>
                    <TableCell align='center'>From QAR</TableCell>
                    <TableCell align='center'>Gap days</TableCell>
                    <TableCell align='center'>Max qty</TableCell>
                    <TableCell align='center'>Status</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell colSpan={9} align='center'>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align='center'>
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => {
                      const image = Array.isArray(row.images) ? row.images[0] : '';
                      return (
                      <TableRow key={row.id}>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {image ? (
                            <Avatar
                              alt={row.labelEn}
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(image, assetUrl)}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align='center'>{row.labelEn}</TableCell>
                        <TableCell align='center'>{row.service_vendor?.name}</TableCell>
                        <TableCell align='center'>{row.service_category?.labelEn}</TableCell>
                        <TableCell align='center'>{row.basePrice}</TableCell>
                        <TableCell align='center'>{row.orderAvailabilityGapDays}</TableCell>
                        <TableCell align='center'>{row.maxQty}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={row.activeState ? 'success' : 'error'}
                          >
                            {row.activeState ? 'Active' : 'Inactive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            component={RouterLink}
                            to={`/event-service-product/edit/${encryptString(row.id)}`}
                          >
                            <Iconify icon='eva:edit-fill' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(
                Math.max(Math.ceil(totalRecords / limit) - 1, 0),
                currentPage
              )}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
